<template>
  <div
    class="c-sharing-settings-visibility-selector"
    :class="[
      selectorBackgroundClass,
      { 'min-w-[120px]': display === 'visibility' },
    ]"
  >
    <!-- Read-only mode: show based on display mode -->
    <template v-if="readOnly">
      <!-- Display mode: fields - show field count with tooltip -->
      <template v-if="display === 'fields'">
        <div v-if="showFieldsPopover">
          <BittsTooltip
            :placement="tooltipPlacement"
            :mount-to-body="shouldMountToBody"
            @menu-visible="getSourcesWithFields"
          >
            <BittsTag
              color="secondary"
              size="small"
              :text="`${sourceFields.length} fields`"
            />
            <template #title>
              <div v-if="hasSources" class="flex flex-col gap-8 p-4">
                <div
                  v-for="(source, index) in sources"
                  :key="`source__${source.table}__${index}`"
                >
                  <span class="mb-8">
                    <strong>{{ source.table }}</strong>
                  </span>
                  <br />
                  <div class="flex flex-wrap gap-4 mt-4">
                    <BittsTag
                      v-for="field in source.fields"
                      :key="`field__${field.id}`"
                      size="x-small"
                      color="field-tooltip"
                      :text="field.display_name"
                    />
                  </div>
                </div>
              </div>
              <div v-else class="p-8 text-neutral-800 text-sm">
                {{
                  sourcesLoading
                    ? 'Loading...'
                    : 'Share data with this partner to see fields'
                }}
              </div>
            </template>
          </BittsTooltip>
        </div>
      </template>

      <!-- Display mode: visibility - show visibility tag -->
      <template v-else>
        <BittsTag
          :color="visibilityColor"
          size="small"
          :text="visibilityLabel"
          :class="{
            'c-sharing-settings-visibility__tag--info-border':
              visibility === SHARING || visibility === GREENFIELD_SHARING,
            'c-sharing-settings-visibility__tag--warning-border':
              visibility === OLAPS_ONLY,
            'c-sharing-settings-visibility__tag--neutral-border':
              visibility === HIDDEN,
          }"
        />
      </template>
    </template>

    <!-- Editable mode: show dropdown -->
    <BittsSelect
      v-else
      v-model="selectedVisibility"
      :options="visibilityOptions"
      :searchable="false"
      dropdown-class-name="c-sharing-settings-visibility-selector__dropdown"
      :dropdown-match-select-width="false"
      :disabled="disabled"
      :list-height="300"
      :use-disabled-options="isGreenfieldSharingBlocked"
      :disabled-option-content="disabledOptionContent"
      :aria-label="ariaLabel"
      @change="handleChange"
    >
      <template #option="{ option }">
        <div class="flex flex-col">
          <div
            class="c-sharing-settings-visibility__option-label flex items-center gap-8"
            :class="{
              'c-sharing-settings-visibility__option-label--overlaps':
                option.value === OLAPS_ONLY,
              'c-sharing-settings-visibility__option-label--not-overlaps':
                option.value !== OLAPS_ONLY,
            }"
          >
            <p>{{ option.label }}</p>
            <BittsTag v-if="option.tag" color="info" size="x-small">
              {{ option.tag }}
            </BittsTag>
          </div>
          <div class="c-sharing-settings-visibility__option-description">
            <p>{{ option.description }}</p>
          </div>
        </div>
      </template>
    </BittsSelect>
  </div>
</template>

<script setup lang="ts">
import {
  BittsSelect,
  BittsTag,
  BittsTooltip,
  PlacementType,
} from '@crossbeam/bitts';

import { computed, ref, watch } from 'vue';

import usePopulationShares from '@/composables/usePopulationShares';
import {
  GREENFIELD_SHARING,
  HIDDEN,
  NEW_SHARING_TYPES_MAP,
  OLAPS_ONLY,
  SHARING,
  SharingType,
} from '@/constants/data_shares';
import {
  IncomingDataShare,
  OutgoingSharingRule,
  SharedField,
} from '@/types/data_shares';
import { Source, SourceField } from '@/types/sources';

type DataShareProp = Partial<OutgoingSharingRule> &
  Partial<IncomingDataShare> & {
    visibility?: SharingType;
    sourceFields?: SharedField[];
  };

type SourceWithFields = Pick<Source, 'id' | 'table'> & {
  fields: SourceField[];
};

type DisabledOptionContent = {
  text: string;
};

type Props = {
  initialVisibility?: SharingType;
  readOnly?: boolean;
  dataShare?: DataShareProp;
  isOwn?: boolean;
  placement?: PlacementType;
  shouldMountToBody?: boolean;
  isGreenfieldSharingBlocked?: boolean;
  disabledOptionContent?: DisabledOptionContent;
  disabled?: boolean;
  display?: 'visibility' | 'fields';
  ariaLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
  initialVisibility: HIDDEN,
  readOnly: false,
  dataShare: undefined,
  isOwn: true,
  placement: undefined,
  shouldMountToBody: true,
  isGreenfieldSharingBlocked: false,
  disabledOptionContent: undefined,
  disabled: false,
  display: 'visibility',
  ariaLabel: undefined,
});

const emit = defineEmits<(e: 'change', visibility: SharingType) => void>();

// Compute tooltip placement based on display mode
const tooltipPlacement = computed(() => {
  // If placement is explicitly provided, use it
  if (props.placement) return props.placement;

  // For fields display in compact mode, use right placement
  if (props.display === 'fields') return 'right';

  // Default to top for other cases
  return 'top';
});

const selectedVisibility = ref(props.initialVisibility);

watch(
  () => props.initialVisibility,
  (visibility) => {
    selectedVisibility.value = visibility;
  },
);

type VisibilityOption = {
  label: string;
  value: SharingType;
  description: string;
  tag?: string;
  disabled?: boolean;
};

const visibilityOptions = computed((): VisibilityOption[] => {
  const baseOptions = Object.values(
    NEW_SHARING_TYPES_MAP,
  ) as VisibilityOption[];

  // Mark greenfield option as disabled if blocked
  if (props.isGreenfieldSharingBlocked) {
    return baseOptions.map((option) => ({
      ...option,
      disabled: option.value === GREENFIELD_SHARING ? true : option.disabled,
    }));
  }

  return baseOptions;
});

function handleChange(visibility: SharingType) {
  emit('change', visibility);
}

const selectorBackgroundClass = computed(() => {
  if (props.disabled) {
    return 'c-sharing-settings-visibility-selector--disabled';
  }

  const classMap: Record<SharingType, string> = {
    [HIDDEN]: 'c-sharing-settings-visibility-selector--neutral',
    [SHARING]: 'c-sharing-settings-visibility-selector--info-ff',
    [GREENFIELD_SHARING]: 'c-sharing-settings-visibility-selector--info-ff',
    [OLAPS_ONLY]: 'c-sharing-settings-visibility-selector--warning-ff',
  };
  return classMap[selectedVisibility.value] ?? '';
});

// Read-only mode state
const visibility = computed(
  () => props.dataShare?.visibility ?? props.initialVisibility,
);

const visibilityLabel = computed(
  () => NEW_SHARING_TYPES_MAP[visibility.value]?.label ?? 'Hidden',
);

const visibilityColor = computed(() => {
  const colorMap: Record<SharingType, string> = {
    [HIDDEN]: 'white',
    [SHARING]: 'info',
    [GREENFIELD_SHARING]: 'info',
    [OLAPS_ONLY]: 'warning',
  };
  return colorMap[visibility.value] ?? 'neutral';
});

const showFieldsPopover = computed(() => {
  // If display mode is explicitly set to 'fields', check if we should show fields
  if (props.display === 'fields') {
    return sourceFields.value.length > 0;
  }
  // Legacy behavior: show fields for 'full' visibility when not own
  return visibility.value?.includes('full') && !props.isOwn;
});

// Source fields popover state
const { sourcesWithFields } = usePopulationShares();

const sourceFields = computed(() => {
  const fields = props.isOwn
    ? props.dataShare?.source_field_ids
    : props.dataShare?.sourceFields;
  return fields ?? [];
});

const sourcesLoading = ref(true);
const sources = ref<SourceWithFields[]>([]);
const hasSources = computed(() => sources.value.length > 0);

function getSourcesWithFields() {
  if (sources.value.length) return;
  sources.value = sourcesWithFields(sourceFields.value, !props.isOwn);
  sourcesLoading.value = false;
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.c-sharing-settings-visibility-selector {
  @apply max-w-[200px] inline-flex items-start;

  /* Force truncation within container */
  :deep(.ant-select) {
    @apply w-full max-w-full;
  }

  :deep(.ant-select-selector) {
    @apply max-w-full;
  }

  :deep(.ant-select-selection-item) {
    @apply !overflow-hidden !whitespace-nowrap !max-w-full !min-w-0;
    text-overflow: ellipsis !important;
    flex: 1 1 auto !important;

    /* Ensure all child elements also truncate */
    > * {
      @apply !overflow-hidden !whitespace-nowrap !max-w-full !min-w-0;
      text-overflow: ellipsis !important;
    }
  }
}

.c-sharing-settings-visibility-selector
  :deep(.ant-select:not(.ant-select-customize-input) .ant-select-selector) {
  @apply h-24 rounded-[28px] shadow-none;
}

/* Background colors based on selected visibility */
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--info-ff
  :deep(.ant-select-selector) {
  @apply bg-info-background-weak/50 border border-info-border rounded-sm!;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--info-ff
  :deep(.ant-select-selection-item) {
  @apply text-info-text;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--info-ff
  :deep(.ant-select-arrow) {
  @apply text-info-accent;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--info-ff
  :deep(.ant-select:hover .ant-select-selector),
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--info-ff
  :deep(.ant-select-focused.ant-select .ant-select-selector) {
  @apply !border-info-border rounded-sm!;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--warning-ff
  :deep(.ant-select-selector) {
  @apply bg-warning-background-weak/50 border border-warning-border rounded-sm!;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--warning-ff
  :deep(.ant-select-selection-item) {
  @apply text-warning-text;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--warning-ff
  :deep(.ant-select-arrow) {
  @apply text-warning-accent;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--warning-ff
  :deep(.ant-select:hover .ant-select-selector),
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--warning-ff
  :deep(.ant-select-focused.ant-select .ant-select-selector) {
  @apply !border-warning-border rounded-sm!;
}

/* Neutral/Hidden */
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--neutral
  :deep(.ant-select-selector) {
  @apply bg-transparent border border-neutral-border-strong rounded-sm!;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--neutral
  :deep(.ant-select-selection-item) {
  @apply text-neutral-text-strong;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--neutral
  :deep(.ant-select-arrow) {
  @apply text-neutral-accent;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--neutral
  :deep(.ant-select:hover .ant-select-selector),
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--neutral
  :deep(.ant-select-focused.ant-select .ant-select-selector) {
  @apply !border-neutral-border-strong rounded-sm!;
}

/* Disabled state - neutral styling without colors */
.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--disabled
  :deep(.ant-select-selector) {
  @apply bg-transparent border border-neutral-border rounded-sm!;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--disabled
  :deep(.ant-select-selection-item) {
  @apply text-neutral-text-weak;
}

.c-sharing-settings-visibility-selector.c-sharing-settings-visibility-selector--disabled
  :deep(.ant-select-arrow) {
  @apply text-neutral-text-weak;
}

/* Hide description and tag in the selected value display */
.c-sharing-settings-visibility-selector
  :deep(.ant-select-selection-item)
  .c-sharing-settings-visibility__option-description,
.c-sharing-settings-visibility-selector
  :deep(.ant-select-selection-item)
  .bitts-tag {
  @apply hidden;
}

/* Force truncation in the nested flex containers from custom option slot */
.c-sharing-settings-visibility-selector :deep(.ant-select-selection-item) {
  div {
    @apply !min-w-0 !max-w-full;
  }

  p {
    @apply !min-w-0 !overflow-hidden !whitespace-nowrap !max-w-full;
    text-overflow: ellipsis !important;
  }
}

/* Option description styling */
.c-sharing-settings-visibility__option-description {
  @apply text-neutral-text-weak text-sm text-wrap;
}
</style>

<!-- Unscoped styles for dropdown (rendered in portal) -->
<style lang="pcss">
@reference "../../css/theme.css";

/* Field tooltip tag custom color */
.field-tooltip {
  @apply bg-neutral-background-weak/12 text-neutral-background-weak;
}

/* Read-only mode borders for BittsTag */
.c-sharing-settings-visibility__tag--info-border.bitts-tag .ant-tag {
  @apply !border !border-info-border;
}

.c-sharing-settings-visibility__tag--warning-border.bitts-tag .ant-tag {
  @apply !border !border-warning-border;
}

.c-sharing-settings-visibility__tag--neutral-border.bitts-tag .ant-tag {
  @apply !border !border-neutral-border;
}

.c-sharing-settings-visibility-selector__dropdown {
  @apply overflow-x-hidden;

  .ant-select-item {
    @apply max-w-[400px];
  }

  .c-sharing-settings-visibility__option-label {
    @apply font-bold text-neutral-text-strong;

    .bitts-tag {
      @apply font-normal;
    }
  }
}
</style>
